import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { FeatherPlus } from "@subframe/core";
import { useState } from "react";

import HotHoldingReadingForm from "@/components/logs/HotHoldingReadingForm";
import { useOrganisation } from "@/contexts/OrganisationContext";
import {
  type BoardMeal,
  type UseOfflineHotHoldingBoardResult,
} from "@/hooks/use-offline-hot-holding-board";

type ReadingType = "DELIVERY" | "SERVICE";

/**
 * Hot holding board (NAU-93). Today-scoped, two lanes: "Awaiting service"
 * (a meal still missing a delivery or service reading) and "Completed". A
 * practitioner logs a delivery (creating the meal + first reading) and later
 * adds the service reading to the same meal. Reads/writes go through the
 * offline-first board hook so submissions survive a lost connection.
 */
export default function HotHoldingPanel({
  board,
}: {
  board: UseOfflineHotHoldingBoardResult;
}) {
  const [showDelivery, setShowDelivery] = useState(false);
  const [openReadingMealId, setOpenReadingMealId] = useState<string | null>(
    null,
  );

  // Org-group "Hot holding policy" floor (NAU-220). Cached at login via the
  // organisations list, so the out-of-range warning stays correct offline;
  // the form falls back to the default when unset.
  const { activeOrg } = useOrganisation();
  const minTempC = activeOrg?.hotHoldingMinTempC;

  const handleLogDelivery = async (args: {
    mealName?: string;
    temperature: number;
    notes?: string;
  }) => {
    await board.logDelivery({
      mealName: args.mealName ?? "",
      temperature: args.temperature,
      notes: args.notes,
    });
    setShowDelivery(false);
  };

  const handleAddReading =
    (meal: BoardMeal) =>
    async (args: {
      readingType: ReadingType;
      temperature: number;
      notes?: string;
    }) => {
      await board.addReading({
        ...(meal.localSyncState
          ? { mealLocalId: meal.id }
          : { mealServerId: meal.id }),
        readingType: args.readingType,
        temperature: args.temperature,
        notes: args.notes,
      });
      setOpenReadingMealId(null);
    };

  if (board.isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-12">
        <span className="text-body font-body text-subtext-color">
          Loading meals…
        </span>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex w-full items-center justify-end">
        <Button
          icon={showDelivery ? undefined : <FeatherPlus />}
          onClick={() => setShowDelivery((s) => !s)}
          variant={showDelivery ? "neutral-secondary" : "brand-primary"}
          size="medium"
        >
          {showDelivery ? "Cancel" : "Log a delivery"}
        </Button>
      </div>

      {showDelivery && (
        <div className="w-full rounded-lg border border-neutral-200 p-4">
          <HotHoldingReadingForm
            mode="delivery"
            onSubmit={handleLogDelivery}
            isSubmitting={false}
            minTempC={minTempC}
          />
        </div>
      )}

      <Lane
        title="Awaiting service"
        emptyText="No meals awaiting a service reading."
        meals={board.awaitingService}
        openReadingMealId={openReadingMealId}
        setOpenReadingMealId={setOpenReadingMealId}
        onAddReading={handleAddReading}
        minTempC={minTempC}
      />
      <Lane
        title="Completed"
        emptyText="No completed meals yet today."
        meals={board.completed}
        openReadingMealId={openReadingMealId}
        setOpenReadingMealId={setOpenReadingMealId}
        onAddReading={handleAddReading}
        minTempC={minTempC}
      />
    </div>
  );
}

function Lane({
  title,
  emptyText,
  meals,
  openReadingMealId,
  setOpenReadingMealId,
  onAddReading,
  minTempC,
}: {
  title: string;
  emptyText: string;
  meals: BoardMeal[];
  openReadingMealId: string | null;
  setOpenReadingMealId: (id: string | null) => void;
  onAddReading: (
    meal: BoardMeal,
  ) => (args: {
    readingType: ReadingType;
    temperature: number;
    notes?: string;
  }) => Promise<void>;
  minTempC?: number;
}) {
  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex items-center gap-2">
        <span className="text-body-bold font-body-bold text-default-font">
          {title}
        </span>
        <Badge variant="neutral">{String(meals.length)}</Badge>
      </div>
      {meals.length === 0 ? (
        <span className="text-caption font-caption text-subtext-color">
          {emptyText}
        </span>
      ) : (
        meals.map((meal) => (
          <div
            key={meal.id}
            className="flex w-full flex-col gap-3 rounded-lg border border-neutral-200 p-4"
          >
            <div className="flex w-full items-center gap-2">
              <span className="grow text-body-bold font-body-bold text-default-font">
                {meal.mealName}
              </span>
              {meal.localSyncState === "pending" && (
                <Badge variant="neutral">Syncing…</Badge>
              )}
              {meal.localSyncState === "error" && (
                <Badge variant="error">Sync failed</Badge>
              )}
            </div>

            <div className="flex w-full flex-col gap-2">
              {meal.readings.map((reading) => (
                <div
                  key={reading.id}
                  className="flex w-full flex-col gap-1 border-l-2 border-neutral-200 pl-3"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-caption-bold font-caption-bold text-default-font">
                      {reading.readingType === "DELIVERY"
                        ? "Delivery"
                        : "Service"}
                    </span>
                    <Badge
                      variant={reading.isOutOfRange ? "warning" : "success"}
                    >
                      {reading.temperature}°C
                    </Badge>
                    <span className="text-caption font-caption text-subtext-color">
                      {new Date(reading.recordedAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                  {reading.notes && (
                    <span className="text-caption font-caption text-subtext-color">
                      {reading.notes}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {openReadingMealId === meal.id ? (
              <div className="w-full rounded-md border border-neutral-200 p-3">
                <HotHoldingReadingForm
                  mode="reading"
                  onSubmit={onAddReading(meal)}
                  isSubmitting={false}
                  minTempC={minTempC}
                />
              </div>
            ) : (
              <Button
                icon={<FeatherPlus />}
                onClick={() => setOpenReadingMealId(meal.id)}
                variant="neutral-secondary"
                size="small"
              >
                Add reading
              </Button>
            )}
          </div>
        ))
      )}
    </div>
  );
}
